import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { setResponseValue } from "../api-response/api-response";
import PaymentSystemService from "../../../service/paymentSystem.service";
import {
  ICurrentPlan,
  IPayment,
  IStripeProduct,
  ITenantUsage,
} from "../../../types/product";
import { getOrgInfo } from "../organization/organization-slice";
import { Addons } from "src/types/stripe";

const initialState: {
  products: IStripeProduct[];
  addons: {
    adminSeatsProducts: any[];
    mailProducts: any[];
    textProducts: any[];
    scheduleProducts: any[];
  };
  tenantUsage: ITenantUsage;
  payments: IPayment[];
  currentPlan: ICurrentPlan;
} = {
  products: [],
  addons: {
    adminSeatsProducts: [],
    mailProducts: [],
    textProducts: [],
    scheduleProducts: [],
  },
  tenantUsage: {
    adminCount: 0,
    clientCount: 0,
    providerCount: 0,
  },
  payments: [],
  currentPlan: {
    currentPlanId: "",
    currentSubscriptionId: "",
    adminSeats: 0,
    planDetail: {
      productId: "",
      title: "",
      features: [],
      price: 0,
      isPopular: false,
      clientLimit: 0,
      adminCovered: 0,
      nextProductId: "",
      additionalAdminPlan: "",
      adminSeatPrice: 0,
    },
    addons: {
      mailbox: {
        priceId: "",
        quantity: 0,
      },
      sms: {
        priceId: "",
      },
      scheduling: {
        priceId: "",
      },
    },
    amount_due: 0,
    discounts: [],
    nextComingInvoice: null,
    subscription: null,
  },
};

const planBillingSlice = createSlice({
  name: "planBillingSlice",
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(
      getPlans.fulfilled,
      (state, action: PayloadAction<IStripeProduct[]>) => {
        if (action.payload) {
          return {
            ...state,
            products: action.payload,
          };
        }
      }
    );
    builder.addCase(
      getAddons.fulfilled,
      (state, action: PayloadAction<any>) => {
        if (action.payload) {
          return {
            ...state,
            addons: action.payload,
          };
        }
      }
    );
    builder.addCase(
      getTenantUsage.fulfilled,
      (state, action: PayloadAction<ITenantUsage>) => {
        if (action.payload) {
          return {
            ...state,
            tenantUsage: action.payload,
          };
        }
      }
    );
    builder.addCase(
      getTenantPayments.fulfilled,
      (state, action: PayloadAction<IPayment[]>) => {
        if (action.payload) {
          return {
            ...state,
            payments: action.payload,
          };
        }
      }
    );
    builder.addCase(
      getTenantPlan.fulfilled,
      (state, action: PayloadAction<ICurrentPlan>) => {
        if (action.payload) {
          return {
            ...state,
            currentPlan: {
              ...action.payload,
            },
          };
        }
      }
    );
    builder.addCase(UpdateSubscription.fulfilled, (state, action) => {
      if (action.payload) {
        return {
          ...state,
          payments: action.payload,
        };
      }
      return state;
    });
  },
});

export const getPlans = createAsyncThunk(
  "planBillingSlice/getPlans",
  async (_, { dispatch }) => {
    dispatch(setResponseValue({ name: "pending", value: true }));
    try {
      const { status, data } = await PaymentSystemService.getPlans();
      if (status) {
        return data;
      }
    } catch (error: any) {
      dispatch(setResponseValue({ name: "error", value: true }));
      dispatch(setResponseValue({ name: "message", value: error?.message }));
    } finally {
      dispatch(setResponseValue({ name: "pending", value: false }));
    }
  }
);

export const getAddons = createAsyncThunk(
  "planBillingSlice/getAddons",
  async (_, { dispatch }) => {
    dispatch(setResponseValue({ name: "pending", value: true }));
    try {
      const { status, data } = await PaymentSystemService.getAddons();
      if (status) {
        return data;
      }
    } catch (error: any) {
      dispatch(setResponseValue({ name: "error", value: true }));
      dispatch(setResponseValue({ name: "message", value: error?.message }));
    } finally {
      dispatch(setResponseValue({ name: "pending", value: false }));
    }
  }
);

export const getTenantUsage = createAsyncThunk(
  "planBillingSlice/getTenantUsage",
  async (_, { dispatch }) => {
    dispatch(setResponseValue({ name: "pending", value: false }));
    try {
      const { status, data } = await PaymentSystemService.getTenantUsage();
      if (status) {
        return data;
      }
    } catch (error: any) {
      dispatch(setResponseValue({ name: "error", value: true }));
      dispatch(setResponseValue({ name: "message", value: error?.message }));
    } finally {
      dispatch(setResponseValue({ name: "pending", value: false }));
    }
  }
);

export const getTenantPlan = createAsyncThunk(
  "planBillingSlice/getTenantPlan",
  async (_, { dispatch }) => {
    dispatch(setResponseValue({ name: "pending", value: false }));
    try {
      const { status, data } = await PaymentSystemService.getTenantPlan();
      if (status) {
        return data;
      }
    } catch (error: any) {
      dispatch(setResponseValue({ name: "error", value: true }));
      dispatch(setResponseValue({ name: "message", value: error?.message }));
    } finally {
      dispatch(setResponseValue({ name: "pending", value: false }));
    }
  }
);

export const getTenantPayments = createAsyncThunk(
  "planBillingSlice/getTenantPayments",
  async (_, { dispatch }) => {
    dispatch(setResponseValue({ name: "pending", value: false }));
    try {
      const { status, data } = await PaymentSystemService.getTenantPayments();
      if (status) {
        return data;
      }
    } catch (error: any) {
      dispatch(setResponseValue({ name: "error", value: true }));
      dispatch(setResponseValue({ name: "message", value: error?.message }));
    } finally {
      dispatch(setResponseValue({ name: "pending", value: false }));
    }
  }
);

export const CheckoutPayment = createAsyncThunk(
  "planBillingSlice/checkoutPayment",
  async (
    {
      productId,
      adminSeats,
      addons,
    }: {
      productId: string;
      adminSeats: number;
      addons?: Addons;
    },
    { dispatch }
  ) => {
    dispatch(setResponseValue({ name: "pending", value: false }));
    try {
      const { status, message, data } =
        await PaymentSystemService.checkoutPayment(
          productId,
          adminSeats,
          addons
        );

      if (status) {
        dispatch(setResponseValue({ name: "success", value: true }));
        dispatch(setResponseValue({ name: "message", value: message }));
        if (data?.url) {
          window.location.replace(data.url);
        }
        return data;
      }
    } catch (error: any) {
      console.log("error:", error);
      dispatch(setResponseValue({ name: "error", value: true }));
      dispatch(setResponseValue({ name: "message", value: error?.message }));
    } finally {
      dispatch(setResponseValue({ name: "pending", value: false }));
    }
  }
);

export const UpdateSubscription = createAsyncThunk(
  "planBillingSlice/UpdateSubscription",
  async (
    {
      productId,
      updateMode,
      adminSeats,
      addons,
      promoCodes,
    }: {
      productId: string;
      updateMode: string;
      adminSeats: number;
      addons?: Addons;
      promoCodes?: string[];
    },
    { dispatch }
  ) => {
    dispatch(setResponseValue({ name: "pending", value: true }));
    try {
      
      // console.log("[DEBUG] UpdateSubscription payload", {
      //   productId,
      //   adminSeats,
      //   ignoreAddons,
      //   addons,
      //   coupon
      // });

      const { status, message, data } =
        await PaymentSystemService.updateSubscription(
          productId,
          updateMode,
          adminSeats,
          addons,
          promoCodes,
        );

      if (status) {
        dispatch(getOrgInfo({ useLoader: true }));
        dispatch(setResponseValue({ name: "success", value: true }));
        dispatch(setResponseValue({ name: "message", value: message }));
        return data;
      }
    } catch (e: any) {
      console.error("[DEBUG] UpdateSubscription error", e);
      dispatch(setResponseValue({ name: "error", value: true }));
      dispatch(
        setResponseValue({
          name: "message",
          value: e?.response?.data?.message || "Something went wrong",
        })
      );
    } finally {
      dispatch(setResponseValue({ name: "pending", value: false }));
    }
  }
);

export default planBillingSlice;
